import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Body1,
  Button,
  Card,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Radio,
  RadioGroup,
  Subtitle1,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useApi } from '../api/client';
import { errorMessage, saveFile } from '../ui/format';

const useStyles = makeStyles({
  page: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalL, alignItems: 'flex-start' },
  card: {
    flex: '1 1 420px',
    maxWidth: '640px',
    padding: tokens.spacingHorizontalXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  actions: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalS },
  list: { margin: 0, paddingLeft: tokens.spacingHorizontalL },
});

export function NewRequestPage() {
  const styles = useStyles();
  const api = useApi();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [venueName, setVenueName] = useState('');
  const [templateId, setTemplateId] = useState('LTPL');
  const templates = useQuery({ queryKey: ['templates'], queryFn: () => api.listTemplates() });

  const create = useMutation({
    mutationFn: () => api.createRequest({ venueName, templateId }),
    onSuccess: (detail) => {
      void qc.invalidateQueries({ queryKey: ['requests'] });
      navigate(`/requests/${detail.request.id}/edit/venue`);
    },
  });

  const download = useMutation({
    mutationFn: async (kind: 'blank' | 'example') =>
      saveFile(await api.requestWorkbookFile(kind, templateId)),
  });

  const upload = useMutation({
    mutationFn: async (file: File) =>
      api.importRequestFile({ name: file.name, bytes: new Uint8Array(await file.arrayBuffer()) }),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['requests'] });
      // Clean load: straight to the review step. Otherwise stay and show what was skipped.
      if (result.messages.length === 0) navigate(`/requests/${result.request.request.id}/edit/review`);
    },
  });
  const loaded = upload.data;

  return (
    <div className={styles.page}>
      <Card className={styles.card}>
        <Title2 as="h1">New venue request</Title2>
        <Body1>
          Name the venue and pick the template company it will be copied from. The intake wizard opens next
          with the template defaults filled in.
        </Body1>
        <Field label="Venue name" required>
          <Input
            value={venueName}
            onChange={(_, d) => setVenueName(d.value)}
            placeholder="Legends - Riverside Arena"
            maxLength={60}
          />
        </Field>
        <Field label="Template">
          <RadioGroup value={templateId} onChange={(_, d) => setTemplateId(d.value)}>
            {(templates.data ?? [{ id: 'LTPL', label: 'Legends template company' }]).map((t) => (
              <Radio key={t.id} value={t.id} label={`${t.id} — ${t.label}`} />
            ))}
          </RadioGroup>
        </Field>
        {create.isError && <MessageBar intent="error">{errorMessage(create.error)}</MessageBar>}
        <div className={styles.actions}>
          <Button
            appearance="primary"
            disabled={!venueName.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Creating…' : 'Create and start intake'}
          </Button>
          <Button onClick={() => navigate('/')}>Cancel</Button>
        </div>
      </Card>

      <Card className={styles.card}>
        <Subtitle1 as="h2">Start from a request file</Subtitle1>
        <Body1>
          One workbook holds the whole request: the intake answers on the Request sheet and the people and
          approvals on the sheets after it. Upload it to create the request, fill every answer and load the
          people in one step.
        </Body1>
        <div className={styles.actions}>
          <Button disabled={download.isPending} onClick={() => download.mutate('blank')}>
            Blank template
          </Button>
          <Button disabled={download.isPending} onClick={() => download.mutate('example')}>
            Filled example
          </Button>
          <Button appearance="primary" disabled={upload.isPending} onClick={() => fileInput.current?.click()}>
            {upload.isPending ? 'Loading…' : 'Upload request file'}
          </Button>
          <input
            ref={fileInput}
            type="file"
            accept=".xlsx"
            hidden
            aria-label="Request file"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) upload.mutate(file);
              e.target.value = '';
            }}
          />
        </div>
        {download.isError && <MessageBar intent="error">{errorMessage(download.error)}</MessageBar>}
        {upload.isError && <MessageBar intent="error">{errorMessage(upload.error)}</MessageBar>}
        {loaded && loaded.messages.length > 0 && (
          <MessageBar intent="warning" layout="multiline">
            <MessageBarBody>
              <MessageBarTitle>
                {loaded.request.request.venueName} created; {loaded.messages.length} item
                {loaded.messages.length === 1 ? '' : 's'} not loaded
              </MessageBarTitle>
              <ul className={styles.list}>
                {loaded.messages.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </MessageBarBody>
          </MessageBar>
        )}
        {loaded && loaded.messages.length > 0 && (
          <div className={styles.actions}>
            <Button
              appearance="primary"
              onClick={() => navigate(`/requests/${loaded.request.request.id}/edit/review`)}
            >
              Open the request
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
